import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ApiError } from '../../api/client';
import {
  createManufacturer,
  createPackage,
  createType,
  type Mount,
} from './api';

/**
 * Adding a reference entry without leaving the form.
 *
 * The shipped books cover what is common, never what is local: JLCPCB is not in
 * the maker list, and neither is the connector somebody's supplier invented. If
 * recording one means opening another screen — or waiting for a release — the
 * name ends up typed into the description instead, where nothing can find it.
 */

interface CommonProps {
  onCreated: (id: string) => void;
  onCancel: () => void;
}

export function AddManufacturer({ onCreated, onCancel }: CommonProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const { error, run, busy } = useCreate((value: string) => createManufacturer({ name: value }));

  return (
    <QuickForm
      title={t('components.addManufacturer')}
      error={error}
      busy={busy}
      onCancel={onCancel}
      onSubmit={() => run(name, onCreated)}
    >
      <input
        value={name}
        onChange={(event) => setName(event.target.value)}
        placeholder={t('components.manufacturer')}
        required
        autoFocus
      />
    </QuickForm>
  );
}

export function AddPackage({ onCreated, onCancel }: CommonProps) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [mount, setMount] = useState<Mount>('other');
  const [size, setSize] = useState('');
  const { error, run, busy } = useCreate(() =>
    createPackage({ code, mount, size_mm: size || null }),
  );

  return (
    <QuickForm
      title={t('components.addPackage')}
      error={error}
      busy={busy}
      onCancel={onCancel}
      onSubmit={() => run(code, onCreated)}
    >
      <input
        value={code}
        onChange={(event) => setCode(event.target.value)}
        placeholder={t('components.package')}
        required
        autoFocus
      />
      <select value={mount} onChange={(event) => setMount(event.target.value as Mount)}>
        <option value="other">{t('components.mount.other')}</option>
        <option value="smd">{t('components.mount.smd')}</option>
        <option value="tht">{t('components.mount.tht')}</option>
      </select>
      <input
        value={size}
        onChange={(event) => setSize(event.target.value)}
        placeholder={t('components.sizeMm')}
      />
    </QuickForm>
  );
}

export function AddSubtype({
  parentId,
  parentName,
  onCreated,
  onCancel,
}: CommonProps & { parentId: string; parentName: string }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const { error, run, busy } = useCreate(() =>
    createType({
      // A code is required and nobody wants to invent one; derive it from the
      // parent and the name, which is unique enough and stays readable.
      code: `${parentName}-${name}`
        .toLowerCase()
        .replace(/[^a-zа-я0-9]+/gi, '_')
        .slice(0, 60),
      name_ru: name,
      parent_id: parentId,
    }),
  );

  return (
    <QuickForm
      title={t('components.addSubtype', { parent: parentName })}
      error={error}
      busy={busy}
      onCancel={onCancel}
      onSubmit={() => run(name, onCreated)}
    >
      <input
        value={name}
        onChange={(event) => setName(event.target.value)}
        placeholder={t('components.subtypeName')}
        required
        autoFocus
      />
      <span className="component-form__parsed">{t('components.subtypeInherits')}</span>
    </QuickForm>
  );
}

function useCreate<T extends { id: string }>(create: (value: string) => Promise<T>) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: ({ value }: { value: string }) => create(value),
    onError: (exc: unknown) => setError(exc instanceof ApiError ? exc.code : 'unknown'),
  });

  return {
    error,
    busy: mutation.isPending,
    run(value: string, onCreated: (id: string) => void) {
      if (!value.trim()) return;
      setError(null);
      mutation.mutate(
        { value: value.trim() },
        {
          onSuccess: (created) => {
            void queryClient.invalidateQueries({ queryKey: ['reference'] });
            onCreated(created.id);
          },
        },
      );
    },
  };
}

function QuickForm({
  title,
  error,
  busy,
  children,
  onSubmit,
  onCancel,
}: {
  title: string;
  error: string | null;
  busy: boolean;
  children: React.ReactNode;
  onSubmit: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="quick-add">
      <strong>{title}</strong>
      <div className="quick-add__fields">{children}</div>
      {error && (
        <p className="panel__error">
          {t(`components.errors.${error}`, t('components.errors.unknown'))}
        </p>
      )}
      <div className="quick-add__actions">
        {/* A button, not a nested form: this lives inside the component form and
            a form inside a form submits the wrong one. */}
        <button type="button" onClick={onSubmit} disabled={busy}>
          {t('common.save')}
        </button>
        <button type="button" onClick={onCancel}>
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
}
